import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FileDown, Loader2, Pin, Search, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, cleanParams, downloadFile, formatErr } from "@/lib/api";
import { dayLabel, fmtDate, sameDay } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useRealtime } from "@/context/RealtimeContext";
import { useUserMap, useUsersLite } from "@/hooks/useData";
import { NSelect, Spinner } from "@/components/common";
import DatePicker from "@/components/DatePicker";
import MessageBubble from "@/components/chat/MessageBubble";
import Composer from "@/components/chat/Composer";
import { ChecklistDialog, PollDialog } from "@/components/chat/ChatDialogs";
import TaskDialog from "@/components/tasks/TaskDialog";
import { cn } from "@/lib/utils";

const PAGE = 60;
const NOFILTER = { q: "", sender: "", date_from: "", date_to: "", meeting: "", pinned: "" };
const todayTag = () => `Meeting – ${fmtDate(new Date().toISOString())}`;

function MeetingTagControl({ tag, setTag, meetings }) {
  const [custom, setCustom] = useState("");
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="meeting-tag-control" className={cn("flex h-8 items-center gap-1 rounded-full border px-3 text-xs font-semibold", tag ? "border-accent bg-accent/15" : "bg-card text-muted-foreground")}>
          <Tag className="h-3.5 w-3.5" />{tag || "No meeting tag"}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 space-y-2" align="start">
        <p className="label-xs">Tag new messages to</p>
        <Button size="sm" variant="outline" className="w-full justify-start" onClick={() => setTag(todayTag())} data-testid="meeting-tag-today">{todayTag()}</Button>
        {meetings.slice(0, 4).filter((m) => m.tag !== todayTag()).map((m) => <Button key={m.tag} size="sm" variant="ghost" className="w-full justify-start" onClick={() => setTag(m.tag)}>{m.tag}</Button>)}
        <div className="flex gap-1">
          <Input value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom tag" className="h-8" data-testid="meeting-tag-custom" />
          <Button size="sm" disabled={!custom.trim()} onClick={() => { setTag(custom.trim()); setCustom(""); }} data-testid="meeting-tag-custom-set">Set</Button>
        </div>
        {tag && <Button size="sm" variant="ghost" className="w-full text-destructive" onClick={() => setTag("")} data-testid="meeting-tag-clear">Stop tagging</Button>}
      </PopoverContent>
    </Popover>
  );
}

export default function ChatPage() {
  const { user } = useAuth();
  const { subscribe } = useRealtime();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const userMap = useUserMap();
  const { data: allUsers = [] } = useUsersLite();
  const chatUsers = useMemo(() => allUsers.filter((u) => u.active && ["admin", "partner"].includes(u.role)), [allUsers]);
  const rx = useMemo(() => {
    const names = chatUsers.map((u) => u.name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).sort((a, b) => b.length - a.length);
    return names.length ? new RegExp(`(@(?:${names.join("|")}))`) : null;
  }, [chatUsers]);

  const [msgs, setMsgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [results, setResults] = useState(null);
  const [filters, setFilters] = useState(NOFILTER);
  const [showSearch, setShowSearch] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [taskFrom, setTaskFrom] = useState(null);
  const [tagFor, setTagFor] = useState(null);
  const [tagText, setTagText] = useState("");
  const [poll, setPoll] = useState(false);
  const [checklist, setChecklist] = useState(false);
  const [hl, setHl] = useState(null);
  const [tag, setTagState] = useState(() => localStorage.getItem("pf-meeting-tag") || "");
  const scroller = useRef(null);
  const { data: meetings = [] } = useQuery({ queryKey: ["chat-meetings"], queryFn: async () => (await api.get("/chat/meetings")).data });
  const setTag = (t) => { setTagState(t); localStorage.setItem("pf-meeting-tag", t); };
  const filtering = Object.values(filters).some(Boolean);

  const toBottom = () => requestAnimationFrame(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; });
  const nearBottom = () => { const s = scroller.current; return !s || s.scrollHeight - s.scrollTop - s.clientHeight < 200; };

  const loadLatest = useCallback(async () => {
    try {
      const { data } = await api.get("/chat/messages", { params: { limit: PAGE } });
      setMsgs(data);
      setHasMore(data.length === PAGE);
      toBottom();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadLatest(); }, [loadLatest]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 15000); return () => clearInterval(t); }, []);

  useEffect(() => subscribe((ev) => {
    if (ev.type === "reconnected") loadLatest();
    if (ev.type === "message.new") {
      const stick = nearBottom() || ev.message.sender_id === user.id;
      setMsgs((p) => (p.some((x) => x.id === ev.message.id) ? p : [...p, ev.message]));
      if (ev.message.meeting_tag) qc.invalidateQueries({ queryKey: ["chat-meetings"] });
      if (stick) toBottom();
    }
    if (ev.type === "message.update") {
      const merge = (old, nw) => (nw.poll && old.poll && nw.poll.my_votes === undefined ? { ...nw, poll: { ...nw.poll, my_votes: old.poll.my_votes } } : nw);
      const rep = (p) => p.map((x) => (x.id === ev.message.id ? merge(x, ev.message) : x));
      setMsgs(rep);
      setResults((r) => (r ? rep(r) : r));
      if (ev.message.meeting_tag) qc.invalidateQueries({ queryKey: ["chat-meetings"] });
    }
  }), [subscribe, loadLatest, user.id, qc]);

  useEffect(() => {
    if (!filtering) return setResults(null);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/chat/messages", { params: cleanParams({ ...filters, limit: 200 }) });
        setResults(data);
      } catch (e) {
        toast.error(formatErr(e));
      }
    }, 300);
    return () => clearTimeout(t);
  }, [filters, filtering]);

  const jump = useCallback((id) => {
    if (filtering) setFilters(NOFILTER);
    setTimeout(() => {
      const el = document.getElementById(`msg-${id}`);
      if (el) {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        setHl(id);
        setTimeout(() => setHl(null), 2500);
      } else toast("That message is older — tap 'Load earlier messages' to find it");
    }, 100);
  }, [filtering]);

  useEffect(() => {
    const m = params.get("m");
    if (m && !loading) jump(m);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, loading]);

  const loadEarlier = async () => {
    const s = scroller.current;
    const h = s.scrollHeight;
    const { data } = await api.get("/chat/messages", { params: { limit: PAGE, before: msgs[0]?.created_at } });
    setMsgs((p) => [...data, ...p]);
    setHasMore(data.length === PAGE);
    requestAnimationFrame(() => { s.scrollTop = s.scrollHeight - h; });
  };

  const saveTag = async () => {
    try {
      await api.patch(`/chat/messages/${tagFor.id}/meeting`, { meeting_tag: tagText });
      setTagFor(null);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const exportPdf = async () => {
    try {
      await downloadFile("/chat/meetings/export", { tag: filters.meeting }, `MOM-${filters.meeting}.pdf`);
    } catch (e) {
      toast.error(formatErr(e));
    }
  };

  const localUpdate = (nm) => {
    const rep = (p) => p.map((x) => (x.id === nm.id ? nm : x));
    setMsgs(rep);
    setResults((r) => (r ? rep(r) : r));
  };
  const list = results || msgs;
  const setF = (k) => (v) => setFilters((p) => ({ ...p, [k]: v }));
  return (
    <div className="-mx-4 -my-6 -mb-32 flex h-[calc(100dvh-8rem)] flex-col sm:-mx-6 md:-mb-12 md:h-[calc(100dvh-4rem)] lg:-mx-10" data-testid="chat-page">
      <div className="flex flex-wrap items-center gap-2 border-b bg-card px-4 py-2.5">
        <h1 className="mr-auto font-display text-lg font-extrabold" data-testid="chat-title">MOM Chat</h1>
        <button onClick={() => setShowSearch((s) => !s)} data-testid="chat-search-toggle" className={cn("flex h-8 items-center gap-1 rounded-full border px-3 text-xs font-semibold", showSearch && "border-primary text-primary")}><Search className="h-3.5 w-3.5" />Search</button>
        <button onClick={() => setF("pinned")(filters.pinned ? "" : "true")} data-testid="chat-pinned-toggle" className={cn("flex h-8 items-center gap-1 rounded-full border px-3 text-xs font-semibold", filters.pinned && "border-primary bg-primary text-primary-foreground")}><Pin className="h-3.5 w-3.5" />Pinned</button>
        <div className="w-44"><NSelect testid="chat-meeting-filter" value={filters.meeting} onChange={setF("meeting")} placeholder="All meetings" options={meetings.map((m) => ({ value: m.tag, label: `${m.tag} (${m.count})` }))} className="h-8 text-xs" /></div>
        {filters.meeting && <Button size="sm" variant="outline" className="h-8" onClick={exportPdf} data-testid="chat-export-pdf"><FileDown className="mr-1 h-3.5 w-3.5" />PDF</Button>}
      </div>
      {showSearch && (
        <div className="grid grid-cols-2 gap-2 border-b bg-card/60 px-4 py-2 md:grid-cols-5" data-testid="chat-search-panel">
          <Input className="col-span-2 h-10" placeholder="Keyword" value={filters.q} onChange={(e) => setF("q")(e.target.value)} data-testid="chat-search-input" />
          <NSelect testid="chat-sender-filter" value={filters.sender} onChange={setF("sender")} placeholder="Anyone" options={chatUsers.map((u) => ({ value: u.id, label: u.name }))} />
          <DatePicker testid="chat-date-from" value={filters.date_from} onChange={setF("date_from")} />
          <DatePicker testid="chat-date-to" value={filters.date_to} onChange={setF("date_to")} />
        </div>
      )}
      {filtering && (
        <div className="flex items-center justify-between bg-accent/15 px-4 py-1.5 text-xs" data-testid="chat-filter-banner">
          <span>{results ? `${results.length} matching messages` : "Searching…"}</span>
          <button onClick={() => setFilters(NOFILTER)} className="flex items-center gap-1 font-semibold" data-testid="chat-clear-filters"><X className="h-3.5 w-3.5" />Clear</button>
        </div>
      )}
      <div ref={scroller} className="flex-1 space-y-3 overflow-y-auto bg-secondary/30 px-3 py-4 sm:px-6" data-testid="chat-messages">
        {loading ? <Spinner /> : (
          <>
            {!filtering && hasMore && <div className="text-center"><Button variant="outline" size="sm" onClick={loadEarlier} data-testid="chat-load-earlier">Load earlier messages</Button></div>}
            {!list.length && <p className="py-16 text-center text-sm text-muted-foreground" data-testid="chat-empty">{filtering ? "No messages match" : "No messages yet — start the conversation"}</p>}
            {list.map((m, i) => (
              <Fragment key={m.id}>
                {(i === 0 || !sameDay(list[i - 1].created_at, m.created_at)) && (
                  <div className="flex justify-center"><span className="rounded-full bg-card px-3 py-0.5 text-[11px] font-semibold text-muted-foreground shadow-sm" data-testid="chat-date-separator">{dayLabel(m.created_at)}</span></div>
                )}
                <MessageBubble m={m} me={user} userMap={userMap} now={now} rx={rx} highlight={hl === m.id}
                  onReply={(x) => { setEditing(null); setReplyTo(x); }} onEdit={(x) => { setReplyTo(null); setEditing(x); }}
                  onTask={setTaskFrom} onUpdate={localUpdate} onTag={(x) => { setTagFor(x); setTagText(x.meeting_tag || todayTag()); }} onJump={jump} />
              </Fragment>
            ))}
          </>
        )}
      </div>
      <div className="flex items-center gap-2 border-t bg-card px-3 pt-2"><MeetingTagControl tag={tag} setTag={setTag} meetings={meetings} /></div>
      <Composer me={user} chatUsers={chatUsers} replyTo={replyTo} editing={editing} meetingTag={tag}
        onCancel={() => { setReplyTo(null); setEditing(null); }} onPoll={() => setPoll(true)} onChecklist={() => setChecklist(true)} />
      <PollDialog open={poll} onOpenChange={setPoll} meetingTag={tag} />
      <ChecklistDialog open={checklist} onOpenChange={setChecklist} meetingTag={tag} />
      <TaskDialog open={!!taskFrom} onOpenChange={(o) => !o && setTaskFrom(null)}
        defaults={taskFrom ? { title: (taskFrom.text || taskFrom.poll?.question || taskFrom.checklist?.title || "Follow up").slice(0, 120), description: taskFrom.text, source_message_id: taskFrom.id } : undefined} />
      <Dialog open={!!tagFor} onOpenChange={(o) => !o && setTagFor(null)}>
        <DialogContent data-testid="tag-message-dialog">
          <DialogHeader><DialogTitle>Tag message to meeting</DialogTitle></DialogHeader>
          <Input value={tagText} onChange={(e) => setTagText(e.target.value)} list="meeting-tags" data-testid="tag-message-input" placeholder="Meeting – DD-MM-YYYY" />
          <datalist id="meeting-tags">{meetings.map((m) => <option key={m.tag} value={m.tag} />)}</datalist>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setTagText(""); }} data-testid="tag-message-clear">Clear</Button>
            <Button onClick={saveTag} data-testid="tag-message-save"><Loader2 className="hidden" />Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
